import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DOWNLOAD_ITEMS, CONTACT_ITEMS } from '../constants';
import { SupportFaq } from '../SupportFaq';
import {
  BookOpen,
  Mail,
  FileText,
  Shield,
  Clock,
  AlertTriangle,
  Headphones,
  ArrowUpRight,
  Download,
} from 'lucide-react';

const downloadTitleMap: Record<string, string> = {
  'WYNDME App': 'downloadsPage.items.app',
  'Firmware': 'downloadsPage.items.firmware',
  'User Manual': 'downloadsPage.items.userManual',
  'Release Notes': 'downloadsPage.items.releaseNotes',
  'Compliance Documents': 'downloadsPage.items.complianceDocuments',
};

const appDownloadItems = DOWNLOAD_ITEMS.filter(
  (item) => item.title === 'WYNDME App' && (item.platform === 'iOS' || item.platform === 'Android'),
);

const warrantySectionKeys = ['coverage', 'period', 'exclusions', 'service'];

const warrantyIconMap: Record<string, React.ReactNode> = {
  coverage: <Shield className="w-5 h-5 text-brand-gold" />,
  period: <Clock className="w-5 h-5 text-brand-gold" />,
  exclusions: <AlertTriangle className="w-5 h-5 text-brand-gold" />,
  service: <Mail className="w-5 h-5 text-brand-gold" />,
};

const contactIconMap: Record<string, React.ReactNode> = {
  'Customer Support': <Headphones className="w-6 h-6" />,
  'Sales': <Mail className="w-6 h-6" />,
};

const contactKeyMap: Record<string, string> = {
  'Customer Support': 'customerSupport',
  'Sales': 'sales',
};

interface SupportSectionHeadingProps {
  title: string;
  icon: React.ReactNode;
  inverted?: boolean;
  children?: React.ReactNode;
}

const SupportSectionHeading: React.FC<SupportSectionHeadingProps> = ({
  title,
  icon,
  inverted = false,
  children,
}) => (
  <div>
    <div
      className={`mb-7 flex h-11 w-11 items-center justify-center rounded-full ${
        inverted ? 'bg-black/10 text-black' : 'bg-brand-gold/10 text-brand-gold'
      }`}
    >
      {icon}
    </div>
    <h2 className="font-display text-3xl font-bold leading-tight md:text-4xl">{title}</h2>
    {children}
  </div>
);

export const SupportPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isChinese = i18n.resolvedLanguage?.startsWith('zh');
  const userManual = isChinese
    ? {
        label: '歪觅 O1 Pro 中文说明书',
        href: '/Files/歪觅O1PRO说明书.pdf',
        downloadName: '歪觅O1PRO说明书.pdf',
      }
    : {
        label: 'WYNDME O1 Pro User Manual',
        href: '/Files/WyndmeO1PRO_UserManual.pdf',
        downloadName: 'WyndmeO1PRO_UserManual.pdf',
      };

  return (
    <div className="min-h-screen bg-brand-black text-white">
      <section className="relative overflow-hidden bg-gradient-to-b from-[#171717] to-brand-black pb-20 pt-32 md:pb-28 md:pt-44">
        <div className="pointer-events-none absolute -right-20 top-8 font-display text-[9rem] font-bold leading-none text-white/[0.025] md:text-[18rem]">
          ?
        </div>
        <div className="pointer-events-none absolute right-[18%] top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-brand-gold/[0.07] blur-[100px]" />
        <div className="container relative mx-auto max-w-6xl px-6 md:px-12">
          <div className="mb-7 flex items-center gap-3">
            <span className="h-2 w-2 flex-shrink-0 rounded-full bg-brand-gold" />
            <p className="whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.08em] text-brand-gold sm:text-xs sm:tracking-[0.24em]">
              {t('supportPage.url')}
            </p>
          </div>
          <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.05] md:text-7xl">
            {t('supportPage.title')}
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid gap-16 lg:grid-cols-[minmax(240px,0.9fr)_minmax(0,2fr)] lg:gap-32">
            <div>
              <SupportSectionHeading
                title={t('supportPage.resources.title')}
                icon={<BookOpen className="h-5 w-5" />}
              />
            </div>

            <div className="rounded-[2rem] bg-white/[0.035] p-6 md:p-8">
              <p className="max-w-2xl text-base leading-7 text-white/55">
                {t('supportPage.resources.desc')}
              </p>
              <div className="mt-8 grid grid-cols-1 gap-x-12 gap-y-3 sm:grid-cols-2">
                <a
                  href={userManual.href}
                  download={userManual.downloadName}
                  className="group inline-flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors hover:bg-white/[0.045] hover:text-brand-gold"
                >
                  {userManual.label}
                  <Download className="h-4 w-4 flex-shrink-0 text-brand-gold transition-transform group-hover:translate-y-0.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SupportFaq />

      <section
        id="app-downloads"
        className="relative scroll-mt-24 overflow-hidden bg-gradient-to-br from-[#18150d] via-[#101010] to-[#0b0b0b] py-24 md:py-32"
      >
        <div className="pointer-events-none absolute -right-24 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-brand-gold/[0.08] blur-[110px]" />
        <div className="container relative mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid gap-16 lg:grid-cols-[minmax(240px,0.9fr)_minmax(0,2fr)] lg:gap-32">
            <div>
              <SupportSectionHeading
                title={t('supportPage.downloads.title')}
                icon={<FileText className="h-5 w-5" />}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              {appDownloadItems.map((item) => (
                <a
                  key={`${item.title}-${item.platform}`}
                  href={item.link}
                  className="group flex min-h-[210px] flex-col justify-between rounded-[1.75rem] bg-white/[0.045] p-6 transition-all hover:-translate-y-1 hover:bg-white/[0.07] md:p-8"
                >
                  <div className="flex items-start justify-between gap-5">
                    <p className="text-xs font-medium tracking-[0.16em] text-white/40">
                      {t(downloadTitleMap[item.title] || item.title)}
                    </p>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-gold/10 text-brand-gold transition-colors group-hover:bg-brand-gold group-hover:text-black">
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold tracking-tight md:text-3xl">
                      {item.platform}
                    </h3>
                    {item.version && (
                      <p className="mt-2 text-xs text-brand-gold">{item.version}</p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-black py-24 md:py-32">
        <div className="container mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid gap-16 lg:grid-cols-[minmax(240px,0.9fr)_minmax(0,2fr)] lg:gap-32">
            <div>
              <SupportSectionHeading
                title={t('warrantyPage.limitedWarranty')}
                icon={<Shield className="h-5 w-5" />}
              >
                <p className="mt-8 max-w-sm text-sm leading-7 text-white/50">
                  {t('warrantyPage.summary')}
                </p>
              </SupportSectionHeading>
            </div>

            <div>
              <div className="grid gap-x-12 gap-y-16 sm:grid-cols-2">
                {warrantySectionKeys.map((key) => (
                  <article key={key}>
                    <div className="mb-4 flex items-center">
                      {warrantyIconMap[key]}
                    </div>
                    <h3 className="mb-5 text-lg font-semibold">
                      {t(`warrantyPage.sections.${key}.title`)}
                    </h3>
                    <p className="max-w-2xl text-sm leading-7 text-white/50">
                      <Trans
                        i18nKey={`warrantyPage.sections.${key}.desc`}
                        components={{
                          support: (
                            <a
                              href="mailto:support@wyndme.cn"
                              className="font-semibold text-brand-gold hover:underline"
                            />
                          ),
                        }}
                      />
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-gold py-24 text-black md:py-32">
        <div className="container mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid gap-16 lg:grid-cols-[minmax(240px,0.9fr)_minmax(0,2fr)] lg:gap-32">
            <div>
              <SupportSectionHeading
                title={t('contactPage.title')}
                icon={<Mail className="h-5 w-5" />}
                inverted
              >
                <p className="mt-8 max-w-sm text-sm leading-7 text-black/60">
                  {t('contactPage.info.desc')}
                </p>
              </SupportSectionHeading>
            </div>

            <div className="space-y-5">
              {CONTACT_ITEMS.map((item) => (
                <a
                  key={item.email}
                  href={`mailto:${item.email}`}
                  className="group flex items-center gap-5 rounded-2xl px-4 py-6 transition-colors hover:bg-black/[0.055] md:px-6 md:py-7"
                >
                  <span className="text-black/60">{contactIconMap[item.category]}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-black/50">
                      {t(`contactPage.categories.${contactKeyMap[item.category]}`)}
                    </span>
                    <span className="mt-2 block break-all text-lg font-semibold md:text-2xl">
                      {item.email}
                    </span>
                  </span>
                  <ArrowUpRight className="h-5 w-5 flex-shrink-0 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="policies" className="relative overflow-hidden bg-[#121212] py-24 md:py-32">
        <div className="pointer-events-none absolute -left-28 bottom-0 h-96 w-96 rounded-full bg-brand-gold/[0.06] blur-[120px]" />
        <div className="container relative mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid gap-16 lg:grid-cols-[minmax(240px,0.9fr)_minmax(0,2fr)] lg:gap-32">
            <div>
              <SupportSectionHeading
                title={t('supportPage.policies.title')}
                icon={<Shield className="h-5 w-5" />}
              />
            </div>

            <div className="space-y-16">
              <article>
                <h3 className="text-xl font-semibold">
                  {t('supportPage.policies.privacy.title')}
                </h3>
                <p className="mt-5 max-w-2xl text-sm leading-8 text-white/50">
                  {t('supportPage.policies.privacy.desc')}
                </p>
              </article>
              <article>
                <h3 className="text-xl font-semibold">
                  {t('supportPage.policies.compliance.title')}
                </h3>
                <p className="mt-5 max-w-2xl text-sm leading-8 text-white/50">
                  {t('supportPage.policies.compliance.desc')}
                </p>
              </article>
              <p className="text-sm text-white/45">
                {t('supportPage.policies.contact')}{' '}
                <a href="mailto:support@wyndme.cn" className="text-brand-gold underline decoration-brand-gold/30 underline-offset-4 hover:decoration-brand-gold">
                  support@wyndme.cn
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
